import React from 'react';
import { asFeaturedIn, earlyBuilderQuotes, founderNote } from '../../mock';
import { ArrowRight, Quote, Info, Sparkles } from 'lucide-react';

export const TrustedBrands = () => {
  return (
    <section className="py-20 lg:py-24 border-y" style={{ backgroundColor: 'var(--yb-bg)', borderColor: 'var(--yb-border)' }}>
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
          <div>
            <p className="text-[13px] uppercase tracking-widest mb-3" style={{ color: 'var(--yb-muted)' }}>Around the web</p>
            <h3 className="font-serif text-3xl lg:text-4xl leading-tight tracking-tight" style={{ color: 'var(--yb-ink)' }}>
              Places we’re part of the<br /><span className="italic">no-code conversation.</span>
            </h3>
            <p className="mt-4 text-[14.5px] max-w-md" style={{ color: 'var(--yb-muted)' }}>
              Communities and publications where non-technical builders swap notes, ship in public, and cheer each other on.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-x-6 gap-y-8">
            {asFeaturedIn.map((b) => (
              <div
                key={b}
                className="font-serif italic text-2xl md:text-[26px] text-center tracking-tight transition-colors"
                style={{ color: 'var(--yb-muted)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--yb-indigo)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--yb-muted)')}
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const FounderNote = () => {
  return (
    <section id="founders-note" className="py-24 lg:py-28 bg-white">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
        <div className="rounded-3xl border p-8 md:p-12" style={{ borderColor: 'var(--yb-border)', background: 'linear-gradient(135deg, rgba(79,70,229,0.06), #FFFFFF 55%, rgba(245,158,11,0.05))' }}>
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border text-[12px]" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
              <Sparkles size={12} style={{ color: 'var(--yb-indigo)' }} /> A note from the founder
            </div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight" style={{ color: 'var(--yb-ink)' }}>
            {founderNote.heading}
          </h2>
          <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed max-w-2xl" style={{ color: 'var(--yb-text)' }}>
            {founderNote.body}
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-serif text-lg" style={{ backgroundColor: 'var(--yb-indigo)' }}>
              Y
            </div>
            <div>
              <p className="text-[13.5px] font-medium" style={{ color: 'var(--yb-ink)' }}>The YouBuild founder</p>
              <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>Non-technical solopreneur • Building in public</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const cardBackgrounds = [
  'linear-gradient(135deg, #EEF2FF, #FFFFFF)',
  'linear-gradient(135deg, #FFFFFF, #F3F1EA)',
  'linear-gradient(135deg, #EEF2FF 0%, #FFFFFF 60%, #FEF3C7 100%)',
];

export const EarlyBuilders = () => {
  return (
    <section id="showcase" className="py-24 lg:py-32" style={{ backgroundColor: 'var(--yb-bg)' }}>
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
          <div className="max-w-2xl">
            <p className="text-[13px] uppercase tracking-widest mb-4" style={{ color: 'var(--yb-muted)' }}>Early builders</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]" style={{ color: 'var(--yb-ink)' }}>
              Built by everyday<br /><span className="italic">creators.</span>
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
              YouBuild is new, and we’re building it in public. The voices below are illustrative
              examples of the kinds of makers we’re here for — not real customers or verified
              testimonials. Real stories will land here as our first builders ship.
            </p>
          </div>
        </div>

        <div className="mb-8 inline-flex items-start gap-2 px-4 py-2.5 rounded-xl bg-white border text-[13px] max-w-2xl" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
          <Info size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--yb-indigo)' }} />
          <span>
            <strong className="font-medium" style={{ color: 'var(--yb-ink)' }}>Illustrative, not real.</strong>{' '}
            These quotes and profiles are placeholders that describe the audience YouBuild is for. No
            company endorsements or statistics have been fabricated.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {earlyBuilderQuotes.map((c, i) => (
            <div
              key={i}
              className="relative rounded-2xl overflow-hidden border p-7 min-h-[300px] flex flex-col justify-between group hover:shadow-lg transition-shadow duration-300"
              style={{ borderColor: 'var(--yb-border)', background: cardBackgrounds[i] }}
            >
              <div>
                <Quote size={22} style={{ color: 'var(--yb-indigo)' }} className="mb-3" />
                <blockquote className="font-serif text-[22px] leading-snug tracking-tight" style={{ color: 'var(--yb-ink)' }}>
                  {c.quote}
                </blockquote>
              </div>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t" style={{ borderColor: 'rgba(23,21,59,0.1)' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium" style={{ backgroundColor: 'rgba(79,70,229,0.12)', color: 'var(--yb-indigo)' }}>
                  {c.initial}
                </div>
                <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>{c.role}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-dashed p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{ borderColor: '#CFCDC5', backgroundColor: 'rgba(255,255,255,0.6)' }}>
          <div className="max-w-xl">
            <p className="text-[13px] uppercase tracking-widest mb-2" style={{ color: 'var(--yb-muted)' }}>
              Customer stories
            </p>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
              Real builder stories, <span className="italic">coming soon.</span>
            </h3>
            <p className="mt-2 text-[14.5px]" style={{ color: 'var(--yb-muted)' }}>
              Shipping something with YouBuild? We’d love to feature you here — with your real
              words, your real story, and your permission.
            </p>
          </div>
          <a
            href="#submit-story"
            className="yb-btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[13.5px] font-semibold whitespace-nowrap"
          >
            Share your story <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
};
